/**
 * Deployment Tracking Slice
 * Manages active deployment polling state with localStorage persistence.
 *
 * Flow:
 *  1. After POST /batch, accepted items are added via `trackDeployments`
 *  2. useDeploymentPolling hook polls GET /api/v1/Deployments/{id} every 15s
 *  3. Statuses are updated via `updateTrackedStatus`
 *  4. Terminal items (Completed/Failed) stop being polled
 *  5. After 7 min, timed-out items are marked and polling stops
 *  6. On mount, `loadFromStorage` restores any non-terminal items
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import {
  TrackedDeployment,
  DeploymentWarning,
  DeploymentWarningEntry,
  TERMINAL_DEPLOYMENT_STATUSES,
  DEPLOYMENT_TRACKING_STORAGE_KEY,
} from '../services/types';

// ========== State ==========

interface DeploymentTrackingState {
  /** Active tracked deployments keyed by deploymentId */
  tracked: Record<string, TrackedDeployment>;
  /** Non-blocking warnings surfaced at completion, retained until dismissed */
  warnings: DeploymentWarningEntry[];
}

// ========== localStorage helpers ==========

function loadTrackedFromStorage(): Record<string, TrackedDeployment> {
  try {
    const raw = localStorage.getItem(DEPLOYMENT_TRACKING_STORAGE_KEY);
    if (!raw) return {};
    const items: TrackedDeployment[] = JSON.parse(raw);
    // Only restore non-terminal items
    const result: Record<string, TrackedDeployment> = {};
    items.forEach(item => {
      if (!TERMINAL_DEPLOYMENT_STATUSES.has(item.apiStatus)) {
        result[item.deploymentId] = item;
      }
    });
    return result;
  } catch {
    return {};
  }
}

function saveTrackedToStorage(tracked: Record<string, TrackedDeployment>) {
  try {
    const items = Object.values(tracked);
    if (items.length === 0) {
      localStorage.removeItem(DEPLOYMENT_TRACKING_STORAGE_KEY);
    } else {
      localStorage.setItem(DEPLOYMENT_TRACKING_STORAGE_KEY, JSON.stringify(items));
    }
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

// ========== Initial state ==========

const initialState: DeploymentTrackingState = {
  tracked: loadTrackedFromStorage(),
  warnings: [],
};

// ========== Slice ==========

const deploymentTrackingSlice = createSlice({
  name: 'deploymentTracking',
  initialState,
  reducers: {
    /**
     * Add new deployments to track after a successful batch deploy.
     */
    trackDeployments: (state, action: PayloadAction<TrackedDeployment[]>) => {
      action.payload.forEach(item => {
        state.tracked[item.deploymentId] = item;
      });
      saveTrackedToStorage(state.tracked);
    },

    /**
     * Update the status of a tracked deployment after polling.
     */
    updateTrackedStatus: (
      state,
      action: PayloadAction<{
        deploymentId: string;
        apiStatus: string;
        errorMessage?: string;
        warnings?: DeploymentWarning[];
      }>,
    ) => {
      const { deploymentId, apiStatus, errorMessage, warnings } = action.payload;
      const item = state.tracked[deploymentId];
      if (item) {
        item.apiStatus = apiStatus;
        item.lastPolledAt = Date.now();
        if (errorMessage) item.errorMessage = errorMessage;

        // Surface any warnings before the terminal item is removed below.
        if (warnings && warnings.length > 0) {
          warnings.forEach(w => {
            const id = `${deploymentId}::${w.label}`;
            if (!state.warnings.some(existing => existing.id === id)) {
              state.warnings.push({
                id,
                deploymentId,
                tenantId: item.tenantId,
                policyName: item.policyName,
                label: w.label,
                message: w.message,
              });
            }
          });
        }

        // Remove terminal items from tracked (they're done)
        if (TERMINAL_DEPLOYMENT_STATUSES.has(apiStatus)) {
          delete state.tracked[deploymentId];
        }
      }
      saveTrackedToStorage(state.tracked);
    },

    /**
     * Dismiss a single surfaced warning by its id.
     */
    dismissWarning: (state, action: PayloadAction<string>) => {
      state.warnings = state.warnings.filter(w => w.id !== action.payload);
    },

    /**
     * Dismiss all surfaced warnings.
     */
    clearWarnings: (state) => {
      state.warnings = [];
    },

    /**
     * Mark a deployment as timed out (7 min exceeded).
     */
    markTimedOut: (state, action: PayloadAction<string>) => {
      const item = state.tracked[action.payload];
      if (item) {
        item.apiStatus = 'TimedOut';
        delete state.tracked[action.payload];
      }
      saveTrackedToStorage(state.tracked);
    },

    /**
     * Remove a specific tracked deployment (e.g., user dismissed).
     */
    removeTracked: (state, action: PayloadAction<string>) => {
      delete state.tracked[action.payload];
      saveTrackedToStorage(state.tracked);
    },

    /**
     * Load tracked deployments from localStorage (called on mount).
     */
    loadFromStorage: (state) => {
      const stored = loadTrackedFromStorage();
      // Merge — don't overwrite active items
      Object.entries(stored).forEach(([id, item]) => {
        if (!state.tracked[id]) {
          state.tracked[id] = item;
        }
      });
    },

    /**
     * Clear all tracked deployments.
     */
    clearAll: (state) => {
      state.tracked = {};
      saveTrackedToStorage(state.tracked);
    },
  },
});

// ========== Actions ==========

export const {
  trackDeployments,
  updateTrackedStatus,
  dismissWarning,
  clearWarnings,
  markTimedOut,
  removeTracked,
  loadFromStorage,
  clearAll,
} = deploymentTrackingSlice.actions;

// ========== Selectors ==========

/** All currently tracked (non-terminal) deployments */
export const selectTrackedDeployments = (state: RootState) =>
  state.deploymentTracking.tracked;

/** Surfaced (undismissed) deployment warnings */
export const selectDeploymentWarnings = (state: RootState) =>
  state.deploymentTracking.warnings;

/** Get tracked deployments for a specific view */
export const selectTrackedByView = (state: RootState, view: 'dashboard' | 'tenant') =>
  Object.values(state.deploymentTracking.tracked).filter(d => d.view === view);

/** Check if a specific entity+tenant combo is being deployed */
export const selectIsDeploying = (
  state: RootState,
  entityId: string,
  tenantId: string,
): TrackedDeployment | undefined =>
  Object.values(state.deploymentTracking.tracked).find(
    d =>
      d.entityId.toLowerCase() === entityId.toLowerCase() &&
      d.tenantId.toLowerCase() === tenantId.toLowerCase(),
  );

/** Count of active deployments */
export const selectActiveDeploymentCount = (state: RootState) =>
  Object.keys(state.deploymentTracking.tracked).length;

export default deploymentTrackingSlice.reducer;
